import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Stack,
  Paper
} from '@mui/material';
import {
  PhotoCamera,
  Upload,
  Close,
  CloudUpload
} from '@mui/icons-material';

interface QuotationUploadProps {
  leadId: number;
  onUploadComplete: (files: File[]) => void;
  uploading: boolean;
}

interface SelectedFile {
  file: File;
  preview: string;
}

export default function QuotationUpload({ leadId, onUploadComplete, uploading }: QuotationUploadProps) {
  const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([]);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;

    setError(null);
    const newFiles: SelectedFile[] = [];

    Array.from(files).forEach((file) => {
      // Validate file type - allow images, PDFs, and Word documents
      const allowedTypes = [
        'image/',
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ];
      const isAllowed = allowedTypes.some(type =>
        type.endsWith('/') ? file.type.startsWith(type) : file.type === type
      );
      // Also check file extension for Word docs (some browsers report different mime types)
      const extension = file.name.toLowerCase().split('.').pop();
      const allowedExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'pdf', 'doc', 'docx'];

      if (!isAllowed && !allowedExtensions.includes(extension || '')) {
        setError('Only image files (JPG, PNG, etc.), PDF files, and Word documents (.doc, .docx) are allowed');
        return;
      }

      // Validate file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }

      // Create preview URL
      const preview = URL.createObjectURL(file);
      newFiles.push({ file, preview });
    });

    setSelectedFiles(prev => [...prev, ...newFiles]);
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => {
      const updated = [...prev];
      URL.revokeObjectURL(updated[index].preview);
      updated.splice(index, 1);
      return updated;
    });
  };

  const handleUpload = () => {
    if (selectedFiles.length === 0) {
      setError('Please select at least one file to upload');
      return;
    }

    const files = selectedFiles.map(sf => sf.file);
    onUploadComplete(files);
  };

  const handleCameraCapture = () => {
    if (cameraInputRef.current) {
      cameraInputRef.current.click();
    }
  };

  const handleFileUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <Box>
      {/* File input elements (hidden) */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,application/pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        multiple
        style={{ display: 'none' }}
        onChange={(e) => handleFileSelect(e.target.files)}
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={(e) => handleFileSelect(e.target.files)}
      />

      {/* Upload options */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" spacing={2} justifyContent="center">
          <Button
            variant="outlined"
            startIcon={<PhotoCamera />}
            onClick={handleCameraCapture}
            disabled={uploading}
          >
            Take Photo
          </Button>
          <Button
            variant="outlined"
            startIcon={<Upload />}
            onClick={handleFileUpload}
            disabled={uploading}
          >
            Choose Files
          </Button>
        </Stack>
      </Box>

      {/* Error message */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Selected files preview */}
      {selectedFiles.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Selected Files ({selectedFiles.length})
          </Typography>
          <Stack spacing={1}>
            {selectedFiles.map((selectedFile, index) => (
              <Paper key={index} sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                {/* File preview */}
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: 1,
                    overflow: 'hidden',
                    bgcolor: 'grey.100',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {selectedFile.file.type.startsWith('image/') ? (
                    <img
                      src={selectedFile.preview}
                      alt={selectedFile.file.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  ) : (
                    <CloudUpload sx={{ color: 'text.secondary' }} />
                  )}
                </Box>

                {/* File info */}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" noWrap>
                    {selectedFile.file.name}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {(selectedFile.file.size / 1024 / 1024).toFixed(2)} MB
                  </Typography>
                </Box>

                {/* Remove button */}
                <Button
                  size="small"
                  color="error"
                  onClick={() => removeFile(index)}
                  disabled={uploading}
                  startIcon={<Close />}
                >
                  Remove
                </Button>
              </Paper>
            ))}
          </Stack>
        </Box>
      )}

      {/* Upload button */}
      {selectedFiles.length > 0 && (
        <Box sx={{ textAlign: 'center' }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleUpload}
            disabled={uploading}
            startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <CloudUpload />}
          >
            {uploading ? 'Uploading...' : `Upload ${selectedFiles.length} File${selectedFiles.length === 1 ? '' : 's'}`}
          </Button>
        </Box>
      )}

      {selectedFiles.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
          <CloudUpload sx={{ fontSize: 48, mb: 1 }} />
          <Typography variant="body2">
            No files selected. Use the buttons above to add quotation documents.
          </Typography>
        </Box>
      )}
    </Box>
  );
}